<script setup lang="ts">
import { computed } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import type { TLiveQuestion, TLiveReveal } from '@/store/liveTrivia/types'

/**
 * A live question, styled after the ui-kit `Question` choices.
 * The ui-kit component decides correctness from the question's answer, which the live server withholds
 * until reveal, and it has no lock-in mode — so live play renders its own choices here.
 */

const { Icon } = UIKit

const {
    question,
    lockedChoiceIndex = null,
    canAnswer = false,
    reveal = null,
    isDarkMode = false,
} = defineProps<{
    question: TLiveQuestion
    lockedChoiceIndex?: number | null
    canAnswer?: boolean
    reveal?: TLiveReveal | null
    isDarkMode?: boolean
}>()

const emit = defineEmits<{
    selectChoice: [choiceIndex: number]
}>()

const totalResponseCount = computed(() => {
    if (!reveal) {
        return 0
    }
    return reveal.distribution.reduce((sum, count) => sum + count, 0) + reveal.noAnswerCount
})

const getChoicePercent = (choiceIndex: number) => {
    if (!reveal || totalResponseCount.value === 0) {
        return 0
    }
    return Math.round(((reveal.distribution[choiceIndex] ?? 0) / totalResponseCount.value) * 100)
}

const getChoiceAriaLabel = (choiceIndex: number) => {
    const letter = String.fromCharCode(65 + choiceIndex)
    const choiceText = new DOMParser().parseFromString(question.choices[choiceIndex] ?? '', 'text/html')
        .body.textContent ?? ''
    const parts = [ `Choice ${letter}: ${choiceText}` ]
    if (reveal) {
        if (reveal.correctIndex === choiceIndex) {
            parts.unshift('Correct answer')
        }
        if (lockedChoiceIndex === choiceIndex) {
            parts.push('your answer')
        }
        parts.push(`${getChoicePercent(choiceIndex)} percent of players`)
    } else if (lockedChoiceIndex === choiceIndex) {
        parts.push('locked in')
    }
    return parts.join(', ')
}

const handleChoiceClick = (choiceIndex: number) => {
    if (!canAnswer || lockedChoiceIndex !== null) {
        return
    }
    emit('selectChoice', choiceIndex)
}
</script>

<template>
    <div class="live-question">
        <div
            v-if="question.passage"
            class="live-question__passage"
            :class="{ 'live-question__passage--dark': isDarkMode }"
            v-html="question.passage"
        />
        <img
            v-if="question.images?.passageImage"
            class="live-question__image"
            :src="question.images.passageImage.url"
            :alt="question.images.passageImage.altText"
        >
        <div
            class="live-question__prompt"
            :class="{ 'live-question__prompt--dark': isDarkMode }"
            v-html="question.prompt"
        />
        <ul class="live-question__choices" aria-label="Choices">
            <li
                v-for="(choice, choiceIndex) in question.choices"
                :key="`${question.serial}-${choiceIndex}`"
                class="live-question__choice-item"
            >
                <button
                    type="button"
                    class="live-question__choice"
                    :class="{
                        'live-question__choice--dark': isDarkMode,
                        'live-question__choice--answerable': canAnswer && lockedChoiceIndex === null,
                        'live-question__choice--locked': lockedChoiceIndex === choiceIndex && !reveal,
                        'live-question__choice--correct': reveal?.correctIndex === choiceIndex,
                        'live-question__choice--incorrect': !!reveal
                            && lockedChoiceIndex === choiceIndex
                            && reveal.correctIndex !== choiceIndex,
                        'live-question__choice--faded': lockedChoiceIndex !== null
                            && lockedChoiceIndex !== choiceIndex
                            && !reveal,
                    }"
                    :disabled="!canAnswer || lockedChoiceIndex !== null"
                    :aria-pressed="lockedChoiceIndex === choiceIndex ? 'true' : 'false'"
                    :aria-label="getChoiceAriaLabel(choiceIndex)"
                    @click="handleChoiceClick(choiceIndex)"
                >
                    <span
                        v-if="reveal"
                        class="live-question__distribution"
                        :class="{
                            'live-question__distribution--dark': isDarkMode,
                            'live-question__distribution--correct': reveal.correctIndex === choiceIndex,
                        }"
                        :style="{ width: `${getChoicePercent(choiceIndex)}%` }"
                        aria-hidden="true"
                    />
                    <span
                        class="live-question__letter"
                        :class="{ 'live-question__letter--dark': isDarkMode }"
                        aria-hidden="true"
                    >
                        {{ String.fromCharCode(65 + choiceIndex) }}
                    </span>
                    <span class="live-question__choice-text"
                          aria-hidden="true"
                          v-html="choice" />
                    <span
                        v-if="reveal"
                        class="live-question__percent"
                        :class="{ 'live-question__percent--dark': isDarkMode }"
                        aria-hidden="true"
                    >
                        {{ getChoicePercent(choiceIndex) }}%
                    </span>
                    <Icon
                        v-else-if="lockedChoiceIndex === choiceIndex"
                        class="live-question__lock-icon"
                        type="lock"
                        aria-hidden="true"
                    />
                </button>
            </li>
        </ul>
        <div
            v-if="reveal?.explanation"
            class="live-question__explanation"
            :class="{ 'live-question__explanation--dark': isDarkMode }"
        >
            <h3 class="live-question__explanation-title">Explanation</h3>
            <div v-html="reveal.explanation" />
            <img
                v-if="reveal.explanationImage"
                class="live-question__image"
                :src="reveal.explanationImage.url"
                :alt="reveal.explanationImage.altText"
            >
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.live-question {
    &__passage {
        margin-bottom: 16px;
        padding: 12px 14px;
        border-radius: 6px;
        background: $white;
        font-size: 15px;
        line-height: 22px;
        border-left: 3px solid $pewter;

        &--dark {
            background: $brand-black;
            border-left-color: $slate;
        }
    }

    &__image {
        display: block;
        max-width: 100%;
        max-height: 320px;
        margin-bottom: 16px;
    }

    &__prompt {
        margin-bottom: 20px;
        font-size: 17.5px;
        font-weight: 600;
        letter-spacing: -0.1px;
        line-height: 26px;
        color: $brand-black;

        &--dark {
            color: $gray-background;
        }

        @include breakpoint(black-bear) {
            font-size: 16px;
            line-height: 24px;
        }

        :deep(p) {
            margin: 0;
        }

        :deep(img) {
            max-width: 100%;
        }
    }

    &__choices {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__choice-item:not(:last-child) {
        margin-bottom: 9px;
    }

    &__choice {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 48px;
        padding: 11px 15px 11px 12px;
        overflow: hidden;
        border: 1px solid rgba($pewter, 0.85);
        border-radius: 6px;
        box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
        background: $white;
        color: $brand-black;
        font: inherit;
        font-size: 16.5px;
        font-weight: 500;
        letter-spacing: -0.1px;
        line-height: 23px;
        text-align: left;
        cursor: default;
        outline: none;

        @include breakpoint(black-bear) {
            font-size: 16px;
        }

        &:disabled {
            color: inherit;
        }

        &--dark {
            border-color: $slate;
            box-shadow: 0 1px 4px 0 rgba($jet, 0.3);
            background: $brand-black;
            color: $gray-background;
        }

        &--answerable {
            cursor: pointer;

            &:hover {
                border-color: $slate;
                box-shadow: 0 0 0 1px $slate;
            }

            &:focus-visible {
                border-color: $brand-blue;
                box-shadow: 0 0 0 1px $brand-blue;
            }
        }

        &--answerable#{&}--dark {
            &:hover {
                border-color: rgba($white, 0.6);
                box-shadow: 0 0 0 1px rgba($white, 0.6);
            }

            &:focus-visible {
                border-color: $banana-bread;
                box-shadow: 0 0 0 1px $banana-bread;
            }
        }

        &--locked {
            border-color: $brand-blue;
            box-shadow: 0 0 0 1px $brand-blue;
        }

        &--locked#{&}--dark {
            border-color: $banana-bread;
            box-shadow: 0 0 0 1px $banana-bread;
        }

        &--faded {
            opacity: 0.55;
        }

        &--correct {
            border-color: $cadaverous;
            box-shadow: 0 0 0 1px $cadaverous;
        }

        &--correct#{&}--dark {
            border-color: $jungle-green;
            box-shadow: 0 0 0 1px $jungle-green;
        }

        &--incorrect {
            border-color: $pepper;
        }

        &--incorrect#{&}--dark {
            border-color: $rosa;
        }
    }

    &__distribution {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        background: $pearl;
        transition: width 0.6s ease-out;

        &--dark {
            background: $moonlit-ocean;
        }

        &--correct {
            background: $barely-green;
        }

        &--correct#{&}--dark {
            background: color.mix($jungle-green, $brand-black, 25%);
        }
    }

    &__letter {
        position: relative;
        flex-shrink: 0;
        width: 22px;
        margin-right: 8px;
        color: $slate;
        font-size: 14px;
        font-weight: 600;

        &--dark {
            color: rgba($white, 0.6);
        }
    }

    &__choice--locked &__letter {
        color: $brand-blue;
    }

    &__choice--locked#{&}__choice--dark &__letter {
        color: $banana-bread;
    }

    &__choice--correct &__letter {
        color: $cadaverous;
    }

    &__choice--incorrect &__letter {
        color: $pepper;
    }

    &__choice-text {
        position: relative;
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;

        :deep(p) {
            margin: 0;
        }
    }

    &__percent {
        position: relative;
        flex-shrink: 0;
        margin-left: 10px;
        color: $ash;
        font-size: 14px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;

        &--dark {
            color: $fog;
        }
    }

    &__lock-icon {
        position: relative;
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        margin-left: 10px;
        color: $brand-blue;
    }

    &__choice--dark &__lock-icon {
        color: $banana-bread;
    }

    &__explanation {
        margin-top: 24px;
        padding: 16px;
        border-radius: 6px;
        background: $white;
        color: $brand-black;
        font-size: 15px;
        line-height: 22px;

        &--dark {
            background: $brand-black;
            color: $gray-background;
        }

        :deep(p) {
            margin: 0 0 8px;
        }

        :deep(img) {
            max-width: 100%;
        }
    }

    &__explanation-title {
        margin: 0 0 8px;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.4px;
        text-transform: uppercase;
    }
}
</style>
